/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { ErrorIcon, SuccessIcon } from '@coursera/cds-icons';

import { REPORT_STEPS } from 'bundles/enterprise-admin-analytics/components/DrawerPollingSteps';
import type { ReportDownload } from 'bundles/enterprise-admin-analytics/components/ReportsStatusProvider';
import ProgressCircle from 'bundles/ui/components/ProgressCircle';

import _t from 'i18n!nls/enterprise-admin-analytics';

const styles = {
  container: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-200);
    align-items: center;
  `,
  loadingItem: css`
    gap: 0;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
};

const getStatusLabel = (status: REPORT_STEPS, startTime?: number) => {
  switch (status) {
    case REPORT_STEPS.SUCCESS:
      return _t('Your report has been generated successfully');
    case REPORT_STEPS.ERROR:
    case REPORT_STEPS.TIMEOUT_ERROR:
      return _t('Something went wrong. Try again later or contact support.');
    case REPORT_STEPS.LOADING: {
      if (startTime) {
        const elapsedTime = Date.now() - startTime;
        const TWO_MINUTES = 2 * 60 * 1000;
        if (elapsedTime > TWO_MINUTES) {
          return _t("It's taking time. You'll receive an email once it's done");
        }
      }
      return _t("We're generating your report, this may take some time.");
    }
    default:
      return '';
  }
};

export const DownloadItem = ({ download }: { download: ReportDownload }) => {
  const getIconForStatus = (status: REPORT_STEPS, progress?: number) => {
    switch (status) {
      case REPORT_STEPS.SUCCESS:
        return (
          <div role="status">
            <SuccessIcon color="success" size="medium" aria-hidden="true" />
          </div>
        );
      case REPORT_STEPS.ERROR:
      case REPORT_STEPS.TIMEOUT_ERROR:
        return (
          <div role="status">
            <ErrorIcon size="medium" color="error" aria-hidden="true" />
          </div>
        );
      case REPORT_STEPS.LOADING:
        return (
          <div role="status" aria-live="polite">
            <ProgressCircle
              percentComplete={progress || 0}
              backgroundColor="var(--cds-color-neutral-stroke-primary-weak)"
              arcWidth={5}
              diameter={20}
              arcColor="var(--cds-color-emphasis-primary-background-strong)"
              aria-hidden="true"
              emptyColor="var(--cds-color-neutral-stroke-primary-weak)"
            />
          </div>
        );

      default:
        return null;
    }
  };
  return (
    <div css={styles.container} role="listitem">
      {getIconForStatus(download.status, download.progress)}
      <div css={styles.content}>
        <Typography2 component="p" variant="subtitleMedium">
          {download.fileName}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {getStatusLabel(download.status, download.startTime)}
        </Typography2>
      </div>
    </div>
  );
};
